/**
 * Campaign Setup & Workflow System
 * Complete end-to-end campaign creation with agent orchestration
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Briefcase, Users, Target, Database, MessageSquare, 
  TrendingUp, CheckCircle, AlertCircle, Clock, ArrowRight,
  FileText, Building, Heart, Share2, Zap, CheckCircle as CheckCircleIcon,
  ThumbsUp, ThumbsDown, Send, Mail, User, Calendar
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { EmergencyHelpButton } from '../emma/EmergencyHelpButton';
import { EmmaChat } from '../emma/EmmaChat';
import { AgentActivityStream } from '../dashboard/AgentActivityStream';
import { FloatingEmmaButton } from '../emma/FloatingEmmaButton';

// Campaign workflow phases from PRD
export type CampaignPhase = 
  | 'setup'
  | 'knowledge-ingestion'
  | 'stakeholder-mapping'
  | 'jvp-synthesis'
  | 'content-orchestration'
  | 'quality-assurance'
  | 'deployment';

// Campaign alignment entities from your requirements
interface CampaignAlignment {
  clients: Client[];
  vendors: Vendor[];
  partners: Partner[];
  solutions: Solution[];
  icp: Record<string, unknown>; // IdealCustomerProfile - simplified for now
  personas: BuyerPersona[];
  buyerGroups: Array<Record<string, unknown>>; // BuyerGroup[] - simplified for now
  challenges: PersonaChallenge[];
  valueProps: ValueProposition[];
  jointValueProps: JointValueProposition[];
}

interface Client {
  id: string;
  name: string;
  industry: string;
  size: 'startup' | 'smb' | 'mid-market' | 'enterprise';
  status: 'active' | 'prospect' | 'inactive';
  primaryContact: string;
}

interface Vendor {
  id: string;
  name: string;
  category: string;
  capabilities: string[];
  partnershipType: 'primary' | 'secondary' | 'technology';
  brandGuidelines: BrandGuidelines;
}

interface Partner {
  id: string;
  name: string;
  type: 'channel' | 'technology' | 'strategic';
  valueAlignment: number; // 0-1 score
  collaborationHistory: string[];
}

interface Solution {
  id: string;
  name: string;
  category: string;
  vendors: string[]; // vendor IDs
  partners: string[]; // partner IDs
  targetPersonas: string[];
  problemsSolved: string[];
}

interface BuyerPersona {
  id: string;
  title: string;
  department: string;
  seniority: 'individual' | 'manager' | 'director' | 'vp' | 'c-level';
  influence: number; // 0-1 score
  decisionCriteria: string[];
  painPoints: string[];
  preferredContent: string[];
}

interface PersonaChallenge {
  id: string;
  personaId: string;
  challenge: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  businessImpact: string;
  currentSolution?: string;
}

interface ValueProposition {
  id: string;
  vendorId: string;
  title: string;
  description: string;
  targetPersonas: string[];
  benefits: string[];
  differentiators: string[];
  proofPoints: ProofPoint[];
}

interface JointValueProposition {
  id: string;
  vendors: string[];
  partners: string[];
  title: string;
  description: string;
  synergies: string[];
  amplifiedBenefits: string[];
  competitiveAdvantage: string;
  consensusScore: number; // 0-1 score
}

interface BrandGuidelines {
  colors: string[];
  fonts: string[];
  tone: string;
  messaging: string[];
  logoUrls: string[];
}

interface ProofPoint {
  id: string;
  type: 'case-study' | 'testimonial' | 'metric' | 'award';
  title: string;
  description: string;
  source: string;
  credibility: number; // 0-1 score
}

// Knowledge ingestion tracking
interface KnowledgeAsset {
  id: string;
  name: string;
  type: 'document' | 'presentation' | 'data' | 'media';
  status: 'uploaded' | 'processing' | 'indexed' | 'error';
  processingProgress: number; // 0-100
  extractedEntities: number;
  relationshipsFound: number;
  insights: string[];
}

// Agent tracking and coordination
interface AgentActivity {
  agentId: string;
  agentName: string;
  status: 'idle' | 'working' | 'completed' | 'error' | 'waiting';
  currentTask: string;
  progress: number; // 0-100
  lastUpdate: Date;
  dependencies: string[]; // other agent IDs
  outputs: AgentOutput[];
}

interface AgentOutput {
  id: string;
  type: string;
  content: React.ReactNode;
  quality_score: number;
  timestamp: Date;
}

// Campaign state management
interface CampaignWorkflow {
  id: string;
  name: string;
  currentPhase: CampaignPhase;
  alignment: CampaignAlignment;
  knowledgeAssets: KnowledgeAsset[];
  agentActivities: AgentActivity[];
  milestones: CampaignMilestone[];
  messageFramework: MessageFramework;
  createdAt: Date;
  updatedAt: Date;
}

interface CampaignMilestone {
  id: string;
  phase: CampaignPhase;
  title: string;
  description: string;
  status: 'pending' | 'in-progress' | 'completed' | 'blocked';
  completedAt?: Date;
  blockers?: string[];
}

interface MessageFramework {
  coreMessage: string;
  personaMessages: Record<string, string>;
  channelAdaptations: Record<string, string>;
  jvpAlignment: string;
  conversionElements: string[];
}

// ===== STORY 4: PUBLISHING WORKFLOW COMPONENT =====
interface PublishingChannel {
  id: string;
  name: string;
  icon: string;
  connected: boolean;
  lastPublished?: string;
  followers?: number;
  engagementRate?: number;
}

interface PublishingWorkflowProps {
  campaignId: string;
}

const PublishingWorkflow: React.FC<PublishingWorkflowProps> = ({ campaignId }) => {
  const [step, setStep] = useState<'channels' | 'scheduling' | 'review' | 'complete'>('channels');
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [scheduledTime, setScheduledTime] = useState<string>('');
  const [isPublishing, setIsPublishing] = useState(false);

  const channels: PublishingChannel[] = [
    { id: 'linkedin', name: 'LinkedIn', icon: '💼', connected: true, followers: 12500, engagementRate: 8.2, lastPublished: '2 days ago' },
    { id: 'twitter', name: 'Twitter/X', icon: '𝕏', connected: true, followers: 45000, engagementRate: 5.4, lastPublished: '1 day ago' },
    { id: 'email', name: 'Email List', icon: '✉️', connected: true, followers: 8900, engagementRate: 21.3, lastPublished: '3 days ago' },
    { id: 'newsletter', name: 'Newsletter', icon: '📰', connected: false, followers: 5200, engagementRate: 28.5 },
    { id: 'slack', name: 'Slack Workspace', icon: '💬', connected: true, followers: 1200, engagementRate: 12.1 }
  ];

  if (step === 'channels') {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">📢 Choose Publishing Channels</h2>
          <p className="text-gray-600">Select where to publish your campaign content</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {channels.map(channel => (
            <button
              key={channel.id}
              onClick={() => {
                if (!channel.connected) return;
                setSelectedChannels(prev => 
                  prev.includes(channel.id) 
                    ? prev.filter(id => id !== channel.id)
                    : [...prev, channel.id]
                );
              }}
              disabled={!channel.connected}
              className={`p-4 rounded-lg border-2 transition-all text-left ${
                selectedChannels.includes(channel.id)
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              } ${!channel.connected ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-2xl">{channel.icon}</span>
                    <span className="font-semibold text-gray-900">{channel.name}</span>
                    {selectedChannels.includes(channel.id) && <CheckCircle className="w-4 h-4 text-blue-500 ml-auto" />}
                  </div>
                  {!channel.connected && <p className="text-xs text-red-600">Not connected</p>}
                  {channel.connected && channel.lastPublished && <p className="text-xs text-gray-500">Last published: {channel.lastPublished}</p>}
                </div>
              </div>
              {channel.connected && (channel.followers || channel.engagementRate) && (
                <div className="flex gap-3 mt-2 pt-2 border-t border-gray-200 text-xs text-gray-600">
                  {channel.followers && <span>📊 {channel.followers.toLocaleString()} followers</span>}
                  {channel.engagementRate && <span>⚡ {channel.engagementRate}% engagement</span>}
                </div>
              )}
            </button>
          ))}
        </div>

        <div className="flex gap-3 justify-end">
          <button className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={() => selectedChannels.length > 0 && setStep('scheduling')}
            disabled={selectedChannels.length === 0}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next: Schedule
          </button>
        </div>
      </div>
    );
  }

  if (step === 'scheduling') {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">📅 Schedule Publishing</h2>
          <p className="text-gray-600">Choose when to publish your content</p>
        </div>

        <div className="space-y-4">
          <button
            onClick={() => setScheduledTime('')}
            className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
              scheduledTime === '' ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-3">
              <Zap className="w-5 h-5 text-green-600" />
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Publish Now</h3>
                <p className="text-sm text-gray-600">Make your content live immediately</p>
              </div>
              {scheduledTime === '' && <CheckCircle className="w-5 h-5 text-green-600" />}
            </div>
          </button>

          <div className="p-4 rounded-lg border-2 border-gray-200">
            <label className="flex items-center gap-3 cursor-pointer mb-3">
              <input
                type="radio"
                checked={scheduledTime !== ''}
                onChange={() => setScheduledTime(new Date().toISOString().slice(0, 16))}
                className="w-4 h-4"
              />
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Schedule for Later</h3>
                <p className="text-sm text-gray-600">Choose a specific date and time</p>
              </div>
            </label>
            {scheduledTime !== '' && (
              <div className="ml-7 pt-2 border-t border-gray-200">
                <input
                  type="datetime-local"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-3 justify-end">
          <button onClick={() => setStep('channels')} className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
            Back
          </button>
          <button
            onClick={() => setStep('review')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Review & Publish
          </button>
        </div>
      </div>
    );
  }

  if (step === 'review') {
    const reviewChannels = channels.filter(c => selectedChannels.includes(c.id));
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">✅ Review & Confirm</h2>
          <p className="text-gray-600">Double-check your publishing settings</p>
        </div>

        <div className="space-y-4">
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <h3 className="font-semibold text-gray-900 mb-3">📢 Channels</h3>
            <div className="grid grid-cols-2 gap-2">
              {reviewChannels.map(channel => (
                <div key={channel.id} className="flex items-center gap-2 text-sm">
                  <span className="text-lg">{channel.icon}</span>
                  <span className="text-gray-700">{channel.name}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
            <h3 className="font-semibold text-gray-900 mb-2">⏰ Timing</h3>
            <p className="text-sm text-gray-700">
              {scheduledTime ? `Scheduled for ${new Date(scheduledTime).toLocaleString()}` : '🚀 Publishing immediately'}
            </p>
          </div>

          <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <h3 className="font-semibold text-gray-900 mb-3">📊 Estimated Impact</h3>
            <div className="space-y-1 text-sm">
              <p className="text-gray-700">
                Total Reach: <span className="font-bold">{reviewChannels.reduce((sum, c) => sum + (c.followers || 0), 0).toLocaleString()}</span> followers
              </p>
              <p className="text-gray-700">
                Avg Engagement: <span className="font-bold">{(reviewChannels.reduce((sum, c) => sum + (c.engagementRate || 0), 0) / reviewChannels.length).toFixed(1)}%</span>
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-3 justify-end">
          <button onClick={() => setStep('scheduling')} className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
            Back
          </button>
          <button
            onClick={async () => {
              setIsPublishing(true);
              await new Promise(resolve => setTimeout(resolve, 1500));
              setStep('complete');
              setIsPublishing(false);
            }}
            disabled={isPublishing}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isPublishing ? <>
              <span className="animate-spin">⏳</span>
              Publishing...
            </> : <>
              <Share2 className="w-4 h-4" />
              Publish Now
            </>}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="text-center space-y-6">
      <div className="text-6xl mb-4">🎉</div>
      <h2 className="text-3xl font-bold text-gray-900">Published Successfully!</h2>
      <p className="text-gray-600 text-lg">Your campaign content is now live across all selected channels</p>

      <div className="space-y-4">
        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 justify-center">
            <CheckCircle className="w-5 h-5 text-green-600" />
            Publishing Complete
          </h3>
          <div className="space-y-2 text-sm text-gray-700">
            {channels.filter(c => selectedChannels.includes(c.id)).map(channel => (
              <div key={channel.id} className="flex items-center gap-2 justify-center">
                <span className="text-lg">{channel.icon}</span>
                <span>{channel.name}</span>
                <CheckCircle className="w-4 h-4 text-green-600" />
              </div>
            ))}
          </div>
        </div>

        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="font-semibold text-gray-900 mb-2">📊 What's Next?</h3>
          <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
            <li>Monitor engagement metrics in real-time</li>
            <li>Check back in 24 hours for initial performance data</li>
            <li>Optimize future content based on what resonates</li>
          </ul>
        </div>
      </div>

      <div className="flex gap-3 justify-center">
        <button
          onClick={() => {
            setStep('channels');
            setSelectedChannels([]);
            setScheduledTime('');
          }}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          Publish Another
        </button>
        <button className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          View Analytics
        </button>
      </div>
    </div>
  );
};

// ===== STORY 5: APPROVAL WORKFLOW COMPONENT =====
interface ApprovalRequest {
  id: string;
  title: string;
  description: string;
  requestedBy: string;
  stakeholders: Stakeholder[];
  dueDate: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  status: 'pending' | 'approved' | 'rejected' | 'needs_revision';
  approvals: ApprovalStatus[];
  comments: ApprovalComment[];
  attachments?: string[];
}

interface Stakeholder {
  id: string;
  name: string;
  email: string;
  role: string;
  avatar?: string;
}

interface ApprovalStatus {
  stakeholderId: string;
  stakeholderName: string;
  status: 'pending' | 'approved' | 'rejected' | 'commented';
  respondedAt?: string;
  comments?: string;
}

interface ApprovalComment {
  id: string;
  author: string;
  content: string;
  timestamp: string;
  type: 'comment' | 'approval' | 'revision_request';
}

interface ApprovalWorkflowProps {
  campaignId: string;
}

const ApprovalWorkflow: React.FC<ApprovalWorkflowProps> = ({ campaignId }) => {
  const [approvalRequests, setApprovalRequests] = useState<ApprovalRequest[]>([
    {
      id: 'apr-001',
      title: 'Campaign Messaging Framework Review',
      description: 'Review and approve the core messaging for the Q1 2026 campaign',
      requestedBy: 'Sarah Chen (Campaign Manager)',
      stakeholders: [
        { id: 'user-1', name: 'Michael Torres', email: 'michael@company.com', role: 'Chief Marketing Officer', avatar: '👨‍💼' },
        { id: 'user-2', name: 'Jessica Lee', email: 'jessica@company.com', role: 'Product Lead', avatar: '👩‍💼' },
        { id: 'user-3', name: 'David Kim', email: 'david@company.com', role: 'Partner Manager', avatar: '👨‍💼' }
      ],
      dueDate: '2026-01-08',
      priority: 'high',
      status: 'pending',
      approvals: [
        { stakeholderId: 'user-1', stakeholderName: 'Michael Torres', status: 'approved', respondedAt: '2026-01-02 14:30' },
        { stakeholderId: 'user-2', stakeholderName: 'Jessica Lee', status: 'pending' },
        { stakeholderId: 'user-3', stakeholderName: 'David Kim', status: 'pending' }
      ],
      comments: [
        {
          id: 'comment-1',
          author: 'Michael Torres',
          content: 'Great messaging direction! I approve this framework.',
          timestamp: '2026-01-02 14:30',
          type: 'approval'
        }
      ]
    },
    {
      id: 'apr-002',
      title: 'Content Assets Quality Sign-Off',
      description: 'Final review of all campaign content assets (emails, social, landing pages)',
      requestedBy: 'James Martinez (Content Lead)',
      stakeholders: [
        { id: 'user-4', name: 'Rachel Johnson', email: 'rachel@company.com', role: 'Quality Assurance', avatar: '👩‍🔬' },
        { id: 'user-5', name: 'Tom Wilson', email: 'tom@company.com', role: 'Legal Review', avatar: '👨‍⚖️' }
      ],
      dueDate: '2026-01-10',
      priority: 'critical',
      status: 'pending',
      approvals: [
        { stakeholderId: 'user-4', stakeholderName: 'Rachel Johnson', status: 'commented', comments: 'Email subject line needs adjustment', respondedAt: '2026-01-02 11:15' },
        { stakeholderId: 'user-5', stakeholderName: 'Tom Wilson', status: 'pending' }
      ],
      comments: [
        {
          id: 'comment-2',
          author: 'Rachel Johnson',
          content: 'Email subject line needs adjustment - current text might trigger spam filters. Recommend: "Exclusive: How to Optimize Supply Chains"',
          timestamp: '2026-01-02 11:15',
          type: 'comment'
        }
      ]
    }
  ]);

  const [selectedRequest, setSelectedRequest] = useState<ApprovalRequest | null>(approvalRequests[0]);
  const [newComment, setNewComment] = useState('');
  const [showRequestDialog, setShowRequestDialog] = useState(false);

  const handleApprove = (requestId: string, stakeholderId: string) => {
    setApprovalRequests(prev => prev.map(req => {
      if (req.id === requestId) {
        return {
          ...req,
          approvals: req.approvals.map(ap => 
            ap.stakeholderId === stakeholderId ? { ...ap, status: 'approved', respondedAt: new Date().toLocaleString() } : ap
          )
        };
      }
      return req;
    }));
  };

  const handleReject = (requestId: string, stakeholderId: string) => {
    setApprovalRequests(prev => prev.map(req => {
      if (req.id === requestId) {
        return {
          ...req,
          approvals: req.approvals.map(ap => 
            ap.stakeholderId === stakeholderId ? { ...ap, status: 'rejected', respondedAt: new Date().toLocaleString() } : ap
          )
        };
      }
      return req;
    }));
  };

  const handleAddComment = () => {
    if (selectedRequest && newComment.trim()) {
      setApprovalRequests(prev => prev.map(req => {
        if (req.id === selectedRequest.id) {
          return {
            ...req,
            comments: [...req.comments, {
              id: `comment-${Date.now()}`,
              author: 'You',
              content: newComment,
              timestamp: new Date().toLocaleString(),
              type: 'comment'
            }]
          };
        }
        return req;
      }));
      setNewComment('');
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default: return 'bg-blue-100 text-blue-800 border-blue-300';
    }
  };

  const getApprovalStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return <ThumbsUp className="w-4 h-4 text-green-600" />;
      case 'rejected': return <ThumbsDown className="w-4 h-4 text-red-600" />;
      case 'commented': return <MessageSquare className="w-4 h-4 text-blue-600" />;
      default: return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Approval Requests List */}
        <div className="lg:col-span-1 space-y-3">
          <h3 className="text-lg font-semibold text-gray-900">Pending Approvals</h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {approvalRequests.map(request => {
              const approvedCount = request.approvals.filter(a => a.status === 'approved').length;
              const totalRequired = request.approvals.length;
              
              return (
                <button
                  key={request.id}
                  onClick={() => setSelectedRequest(request)}
                  className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                    selectedRequest?.id === request.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-gray-900 text-sm">{request.title}</h4>
                    <Badge className={`text-xs py-0 px-2 ${getPriorityColor(request.priority)}`}>
                      {request.priority}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600 mb-2">
                    <Calendar className="w-3 h-3" />
                    Due {new Date(request.dueDate).toLocaleDateString()}
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div 
                      className="bg-green-600 h-1.5 rounded-full transition-all"
                      style={{ width: `${(approvedCount / totalRequired) * 100}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{approvedCount}/{totalRequired} approved</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Approval Details */}
        {selectedRequest && (
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{selectedRequest.title}</h2>
                  <p className="text-gray-600 text-sm mt-1">Requested by: {selectedRequest.requestedBy}</p>
                </div>
                <Badge className={`text-sm ${getPriorityColor(selectedRequest.priority)}`}>
                  {selectedRequest.priority.toUpperCase()}
                </Badge>
              </div>

              <p className="text-gray-700 mb-6">{selectedRequest.description}</p>

              {/* Stakeholders & Approvals */}
              <div className="space-y-3 mb-6">
                <h3 className="font-semibold text-gray-900">Stakeholder Approvals</h3>
                <div className="space-y-2">
                  {selectedRequest.stakeholders.map((stakeholder, idx) => {
                    const approval = selectedRequest.approvals.find(a => a.stakeholderId === stakeholder.id);
                    return (
                      <div key={stakeholder.id} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-start gap-3 flex-1">
                          <span className="text-2xl">{stakeholder.avatar || '👤'}</span>
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">{stakeholder.name}</h4>
                            <p className="text-sm text-gray-600">{stakeholder.role}</p>
                            {approval?.respondedAt && <p className="text-xs text-gray-500">Responded: {approval.respondedAt}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {approval && getApprovalStatusIcon(approval.status)}
                          <span className="text-sm font-medium capitalize text-gray-700">{approval?.status || 'pending'}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Comments Thread */}
              <div className="space-y-3 mb-6">
                <h3 className="font-semibold text-gray-900">Comments & Feedback</h3>
                <div className="space-y-3 max-h-48 overflow-y-auto bg-gray-50 rounded-lg p-4 border border-gray-200">
                  {selectedRequest.comments.map(comment => (
                    <div key={comment.id} className="pb-3 border-b border-gray-200 last:border-0">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-semibold text-sm text-gray-900">{comment.author}</h4>
                        <span className="text-xs text-gray-500">{comment.timestamp}</span>
                      </div>
                      <p className="text-sm text-gray-700">{comment.content}</p>
                      {comment.type !== 'comment' && (
                        <Badge className={`text-xs mt-2 ${
                          comment.type === 'approval' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {comment.type === 'approval' ? '✓ Approved' : 'Revision Needed'}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>

                {/* Add Comment */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                    placeholder="Add a comment..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  <button
                    onClick={handleAddComment}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    Comment
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4 border-t border-gray-200">
                <button className="flex-1 px-4 py-2 border border-green-300 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 font-medium flex items-center justify-center gap-2">
                  <ThumbsUp className="w-4 h-4" />
                  Approve All
                </button>
                <button className="flex-1 px-4 py-2 border border-orange-300 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 font-medium flex items-center justify-center gap-2">
                  <Mail className="w-4 h-4" />
                  Send Reminder
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Total Pending</p>
          <p className="text-3xl font-bold text-blue-600">{approvalRequests.filter(r => r.status === 'pending').length}</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Total Approved</p>
          <p className="text-3xl font-bold text-green-600">{approvalRequests.filter(r => r.status === 'approved').length}</p>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Avg Response Time</p>
          <p className="text-3xl font-bold text-orange-600">4.2h</p>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Completion Rate</p>
          <p className="text-3xl font-bold text-purple-600">78%</p>
        </div>
      </div>
    </div>
  );
};

// Component props
interface CampaignWorkflowDashboardProps {
  campaignId?: string;
  onCampaignUpdate?: (campaign: CampaignWorkflow) => void;
}

export const CampaignWorkflowDashboard: React.FC<CampaignWorkflowDashboardProps> = ({
  campaignId,
  onCampaignUpdate
}) => {
  // State management
  const [campaign, setCampaign] = useState<CampaignWorkflow | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('overview');

  // Load campaign data
  useEffect(() => {
    if (campaignId) {
      loadCampaignData(campaignId);
    } else {
      initializeNewCampaign();
    }
  }, [campaignId]);

  const loadCampaignData = async (id: string) => {
    setLoading(true);
    try {
      // TODO: Implement actual API call
      const mockCampaign: CampaignWorkflow = {
        id,
        name: "Enterprise ABM Campaign - Manufacturing CFOs",
        currentPhase: 'stakeholder-mapping',
        alignment: {
          clients: [],
          vendors: [],
          partners: [],
          solutions: [],
          icp: {
            industry: 'Manufacturing',
            company_size: 'Enterprise',
            revenue_range: '$500M-$5B',
            pain_points: ['Cost reduction', 'Digital transformation', 'Supply chain optimization']
          } as any,
          personas: [],
          buyerGroups: [],
          challenges: [],
          valueProps: [],
          jointValueProps: []
        },
        knowledgeAssets: [],
        agentActivities: [],
        milestones: [],
        messageFramework: {
          coreMessage: '',
          personaMessages: {},
          channelAdaptations: {},
          jvpAlignment: '',
          conversionElements: []
        },
        createdAt: new Date(),
        updatedAt: new Date()
      };
      setCampaign(mockCampaign);
    } catch (error) {
      console.error('Failed to load campaign:', error);
    } finally {
      setLoading(false);
    }
  };

  const initializeNewCampaign = () => {
    // Initialize new campaign with setup phase
    const newCampaign: CampaignWorkflow = {
      id: `campaign_${Date.now()}`,
      name: "New Campaign",
      currentPhase: 'setup',
      alignment: {
        clients: [],
        vendors: [],
        partners: [],
        solutions: [],
        icp: {} as any,
        personas: [],
        buyerGroups: [],
        challenges: [],
        valueProps: [],
        jointValueProps: []
      },
      knowledgeAssets: [],
      agentActivities: [],
      milestones: [
        {
          id: 'setup',
          phase: 'setup',
          title: 'Campaign Setup',
          description: 'Define campaign objectives and alignment',
          status: 'in-progress'
        },
        {
          id: 'knowledge',
          phase: 'knowledge-ingestion',
          title: 'Knowledge Ingestion',
          description: 'Upload and process all campaign assets',
          status: 'pending'
        },
        {
          id: 'stakeholders',
          phase: 'stakeholder-mapping',
          title: 'Stakeholder Mapping',
          description: 'Map buying committee and personas',
          status: 'pending'
        }
      ],
      messageFramework: {
        coreMessage: '',
        personaMessages: {},
        channelAdaptations: {},
        jvpAlignment: '',
        conversionElements: []
      },
      createdAt: new Date(),
      updatedAt: new Date()
    };
    setCampaign(newCampaign);
  };

  const renderPhaseProgress = () => {
    const phases: { phase: CampaignPhase; title: string; icon: React.ReactNode }[] = [
      { phase: 'setup', title: 'Setup', icon: <Briefcase className="h-4 w-4" /> },
      { phase: 'knowledge-ingestion', title: 'Knowledge', icon: <Database className="h-4 w-4" /> },
      { phase: 'stakeholder-mapping', title: 'Stakeholders', icon: <Users className="h-4 w-4" /> },
      { phase: 'jvp-synthesis', title: 'JVP Synthesis', icon: <Heart className="h-4 w-4" /> },
      { phase: 'content-orchestration', title: 'Content', icon: <MessageSquare className="h-4 w-4" /> },
      { phase: 'quality-assurance', title: 'Quality', icon: <CheckCircle className="h-4 w-4" /> },
      { phase: 'deployment', title: 'Deploy', icon: <TrendingUp className="h-4 w-4" /> }
    ];

    const currentIndex = phases.findIndex(p => p.phase === campaign?.currentPhase);

    return (
      <div className="flex items-center space-x-2 overflow-x-auto pb-2">
        {phases.map((phase, index) => {
          const isCompleted = index < currentIndex;
          const isCurrent = index === currentIndex;
          const isPending = index > currentIndex;

          return (
            <React.Fragment key={phase.phase}>
              <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg border-2 transition-all ${
                isCompleted ? 'bg-green-50 border-green-200 text-green-700' :
                isCurrent ? 'bg-blue-50 border-blue-200 text-blue-700' :
                'bg-gray-50 border-gray-200 text-gray-500'
              }`}>
                {phase.icon}
                <span className="text-sm font-medium whitespace-nowrap">{phase.title}</span>
                {isCompleted && <CheckCircle className="h-4 w-4 text-green-600" />}
                {isCurrent && <Clock className="h-4 w-4 text-blue-600" />}
              </div>
              {index < phases.length - 1 && (
                <ArrowRight className={`h-4 w-4 ${isCompleted ? 'text-green-400' : 'text-gray-300'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const renderCampaignAlignment = () => {
    if (!campaign) return null;

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Clients */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center space-x-2">
              <Building className="h-5 w-5 text-blue-600" />
              <span>Clients</span>
              <Badge variant="secondary">{campaign.alignment.clients.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {campaign.alignment.clients.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-gray-500 text-sm">No clients configured</p>
                <Button variant="secondary" size="small" className="mt-2">
                  Add Client
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {campaign.alignment.clients.map(client => (
                  <div key={client.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div>
                      <div className="font-medium text-sm">{client.name}</div>
                      <div className="text-xs text-gray-500">{client.industry} • {client.size}</div>
                    </div>
                    <Badge variant={client.status === 'active' ? 'default' : 'secondary'}>
                      {client.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Vendors */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center space-x-2">
              <Share2 className="h-5 w-5 text-purple-600" />
              <span>Vendors</span>
              <Badge variant="secondary">{campaign.alignment.vendors.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {campaign.alignment.vendors.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-gray-500 text-sm">No vendors configured</p>
                <Button variant="secondary" size="small" className="mt-2">
                  Add Vendor
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {campaign.alignment.vendors.map(vendor => (
                  <div key={vendor.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div>
                      <div className="font-medium text-sm">{vendor.name}</div>
                      <div className="text-xs text-gray-500">{vendor.category}</div>
                    </div>
                    <Badge variant={vendor.partnershipType === 'primary' ? 'default' : 'secondary'}>
                      {vendor.partnershipType}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Personas */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center space-x-2">
              <Users className="h-5 w-5 text-green-600" />
              <span>Buyer Personas</span>
              <Badge variant="secondary">{campaign.alignment.personas.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {campaign.alignment.personas.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-gray-500 text-sm">No personas mapped</p>
                <Button variant="secondary" size="small" className="mt-2">
                  Map Personas
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {campaign.alignment.personas.map(persona => (
                  <div key={persona.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div>
                      <div className="font-medium text-sm">{persona.title}</div>
                      <div className="text-xs text-gray-500">{persona.department} • {persona.seniority}</div>
                    </div>
                    <div className="text-xs text-gray-600">
                      Influence: {Math.round(persona.influence * 100)}%
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Joint Value Props */}
        <Card className="md:col-span-2 lg:col-span-3">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center space-x-2">
              <Heart className="h-5 w-5 text-red-600" />
              <span>Joint Value Propositions</span>
              <Badge variant="secondary">{campaign.alignment.jointValueProps.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {campaign.alignment.jointValueProps.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-4xl mb-4">🤝</div>
                <p className="text-gray-500 mb-4">No joint value propositions created yet</p>
                <p className="text-sm text-gray-400 mb-4">
                  JVPs align vendor and partner capabilities to create compelling joint narratives
                </p>
                <Button variant="primary" className="mr-2">
                  Create JVP
                </Button>
                <Button variant="secondary">
                  Import Template
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {campaign.alignment.jointValueProps.map(jvp => (
                  <div key={jvp.id} className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium">{jvp.title}</h4>
                      <Badge variant={jvp.consensusScore > 0.8 ? 'default' : 'secondary'}>
                        {Math.round(jvp.consensusScore * 100)}% consensus
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{jvp.description}</p>
                    <div className="flex flex-wrap gap-1">
                      {jvp.synergies.slice(0, 3).map((synergy, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {synergy}
                        </Badge>
                      ))}
                      {jvp.synergies.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{jvp.synergies.length - 3} more
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading campaign workflow...</p>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="text-center py-12">
        <div className="text-4xl mb-4">🎯</div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">No Campaign Selected</h2>
        <p className="text-gray-600 mb-6">Create a new campaign or select an existing one to begin</p>
        <Button variant="primary" onClick={initializeNewCampaign}>
          Create New Campaign
        </Button>
      </div>
    );
  }

  const [showEmmaHelp, setShowEmmaHelp] = useState(false);

  const handleEmmaHelp = () => {
    setShowEmmaHelp(true);
  };

  return (
    <div className="space-y-6 relative">
      {/* Campaign Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl">{campaign.name}</CardTitle>
              <p className="text-gray-600 mt-1">
                Created {campaign.createdAt.toLocaleDateString()} • 
                Phase: <span className="font-medium capitalize">{campaign.currentPhase.replace('-', ' ')}</span>
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="secondary" size="small">
                <FileText className="h-4 w-4 mr-2" />
                Export Report
              </Button>
              <Button variant="primary" size="small">
                <Zap className="h-4 w-4 mr-2" />
                Launch Agents
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {renderPhaseProgress()}
        </CardContent>
      </Card>

      {/* Campaign Alignment */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Campaign Alignment</h3>
        {renderCampaignAlignment()}
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', label: 'Overview', icon: Target },
            { id: 'knowledge', label: 'Knowledge Assets', icon: Database },
            { id: 'agents', label: 'Agent Activity', icon: Zap },
            { id: 'publishing', label: 'Publishing', icon: Share2 },
            { id: 'approvals', label: 'Approvals', icon: CheckCircle },
            { id: 'message-framework', label: 'Message Framework', icon: MessageSquare }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-96">
        {activeTab === 'overview' && (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">📊</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Campaign Overview</h3>
            <p className="text-gray-600">Detailed campaign metrics and progress will be displayed here</p>
          </div>
        )}
        
        {activeTab === 'knowledge' && (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">🧠</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Knowledge Asset Management</h3>
            <p className="text-gray-600">Upload, process, and manage campaign knowledge assets</p>
          </div>
        )}
        
        {activeTab === 'agents' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-96">
            {/* Agent Activity Stream - Two-thirds width */}
            <div className="lg:col-span-2 bg-slate-900 rounded-lg overflow-hidden border border-slate-700">
              <AgentActivityStream 
                campaignId={campaign.id}
                realTimeEnabled={true}
                showFilters={true}
                onAgentHandoff={(fromAgent, toAgent) => {
                  console.log(`Agent handoff: ${fromAgent} → ${toAgent}`);
                }}
              />
            </div>
            
            {/* Agent Status Panel - One-third width */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-4">🤖 Active Agents</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                  <span className="text-sm font-medium text-gray-900">Sofia</span>
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Ready</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-blue-50 rounded">
                  <span className="text-sm font-medium text-gray-900">Casey</span>
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Idle</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-amber-50 rounded">
                  <span className="text-sm font-medium text-gray-900">Riley</span>
                  <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">Research</span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'publishing' && (
          <PublishingWorkflow campaignId={campaign?.id || ''} />
        )}
        
        {activeTab === 'approvals' && (
          <ApprovalWorkflow campaignId={campaign?.id || ''} />
        )}
        
        {activeTab === 'message-framework' && (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">💬</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Message Framework</h3>
            <p className="text-gray-600">Develop and align messaging across all personas and channels</p>
          </div>
        )}
      </div>
      
      {/* EPIC CITE-RECOVERY-001: Emma escape hatch */}
      <div className="fixed bottom-6 right-6 z-50">
        <EmergencyHelpButton 
          onClick={handleEmmaHelp}
          isVisible={!showEmmaHelp}
        />
      </div>
      
      {/* Emma Chat Modal */}
      {showEmmaHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl h-3/4 flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-semibold text-gray-900">Emma - Your Campaign Workflow Assistant</h2>
              <button
                onClick={() => setShowEmmaHelp(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <EmmaChat 
                initialContext={{
                  currentPage: 'workflow-dashboard',
                  campaign: campaign.name,
                  currentPhase: campaign.currentPhase
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Floating Emma Button - P1 Enhancement */}
      <FloatingEmmaButton 
        context="campaign-workflow"
        prefilledMessage={`I need help with ${campaign.currentPhase} phase of my campaign "${campaign.name}"`}
      />
    </div>
  );
};

export default CampaignWorkflowDashboard;
